import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import HeroBanner from '../components/common/HeroBanner/HeroBanner';
import './FAQ.css';

const FAQ = () => {
  useEffect(() => {
    document.title = 'NEST | FAQ';
  }, []);

  const [activeIndex, setActiveIndex] = useState(null);

  const faqs = [
    {
      question: 'What is the typical cost of vehicle transportation services?',
      answer: 'Pricing for vehicle shipping varies based on several factors including distance, vehicle size and weight, carrier type (open or enclosed), current fuel prices, and seasonal demand. For an accurate quote, we recommend contacting our team directly or using our online quote tool. We provide competitive rates tailored to your specific transportation needs.'
    },
    {
      question: 'Can I transport personal items or cargo inside my vehicle during shipping?',
      answer: 'For safety and insurance reasons, we generally recommend not placing items inside your vehicle during transport. Personal belongings may shift during transit and could damage the vehicle interior. Additionally, insurance coverage may be limited for personal items. Please contact our team to discuss your specific situation, as we may be able to accommodate certain requests on a case-by-case basis.'
    },
    {
      question: 'How long does vehicle transportation typically take?',
      answer: 'Transportation duration depends on the distance between pickup and delivery locations. Most shipments across the continental United States take between 3 to 7 days for standard service. Cross-country deliveries typically take 5-7 days. Expedited options are available for shorter timeframes. We\'ll provide estimated delivery windows when you book your shipment.'
    },
    {
      question: 'How far in advance should I schedule my vehicle transport?',
      answer: 'We recommend booking your vehicle transport at least 2-3 weeks before your desired pickup date. This allows sufficient time for scheduling and logistics planning. However, we understand that sometimes transportation is needed urgently. Contact our team to discuss rush or expedited shipping options that may be available.'
    },
    {
      question: 'Do you guarantee specific pickup and delivery dates?',
      answer: 'While we make every effort to meet requested pickup and delivery dates, we cannot guarantee exact dates due to factors beyond our control such as weather, traffic, and carrier scheduling. We provide estimated date ranges and work diligently to meet your timeline. Our team communicates regularly throughout the process to keep you informed of any changes.'
    },
    {
      question: 'Do you guarantee specific pickup and delivery times?',
      answer: 'Similar to dates, we cannot guarantee exact pickup or delivery times. We provide time windows (morning or afternoon) to accommodate your schedule. Our dispatch team will communicate with you 24-48 hours before pickup to confirm the timeframe. We prioritize getting your vehicle shipped on schedule within the agreed-upon window.'
    },
    {
      question: 'Can I track my vehicle during transport?',
      answer: 'Yes! We provide real-time tracking so you can monitor your vehicle\'s location throughout the shipping process. You\'ll receive updates via phone and email. Our customer portal allows you to check status anytime. Our team is also available to answer questions about your shipment\'s progress.'
    },
    {
      question: 'Does vehicle size affect the shipping cost or process?',
      answer: 'Yes, vehicle size and weight do impact pricing. Larger vehicles like SUVs and trucks typically cost more to transport than sedans due to the space they occupy on the carrier. We also consider the vehicle\'s dimensions for routing and carrier selection. When you request a quote, we factor in your vehicle specifications to provide accurate pricing.'
    },
    {
      question: 'Can I ship a vehicle that is not in working condition?',
      answer: 'Absolutely! We regularly transport non-operational vehicles including those with engine problems, transmission issues, or other mechanical failures. Our experienced drivers and carriers are equipped to handle inoperable vehicles safely. Please inform us of your vehicle\'s condition when booking so we can make appropriate arrangements.'
    },
    {
      question: 'Is my vehicle protected with insurance during transport?',
      answer: 'Yes, all vehicles in our care are covered by comprehensive insurance protection. Our carriers maintain liability coverage, and additional coverage options are available for high-value vehicles. We take every precaution to ensure your vehicle arrives in the same condition as when it was picked up. Documentation is provided at pickup and delivery.'
    },
    {
      question: 'What should I do if my vehicle is damaged during transport?',
      answer: 'If you notice any damage upon delivery, document it immediately with photos and notify us right away. Provide a detailed damage report to our claims department. We have insurance coverage for damages occurring during transport. Our team will guide you through the claims process to resolve the issue promptly and fairly.'
    },
    {
      question: 'Do I need to be present for vehicle pickup and delivery?',
      answer: 'It\'s not always necessary for you to be present, but we recommend it. Being available allows you to inspect your vehicle before pickup and after delivery, and to answer any questions the driver might have. If you cannot be present, arrangements can be made with a trusted representative. Communication with our team will confirm the best approach for your situation.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept multiple payment options including credit cards, debit cards, bank transfers, and check payments. A deposit is typically required to reserve your shipment, with the balance due upon delivery. We strive to offer flexible payment options to make the process convenient for our customers. Ask about our payment plans if needed.'
    },
    {
      question: 'Is the deposit refundable if I need to cancel my shipment?',
      answer: 'Deposits are generally refundable if you cancel before your vehicle is picked up. Cancellation requests must be submitted in writing as soon as possible. If your shipment has already begun transport, deposit refund eligibility may be limited. Review our cancellation policy or contact our customer service team for specific details regarding your booking.'
    },
    {
      question: 'Are there any hidden fees or surprise charges?',
      answer: 'We believe in transparent pricing with no hidden fees. The quote we provide includes all standard charges for your shipment. Any additional services (like expedited delivery or specialty handling) are clearly outlined before you confirm your booking. If circumstances change during transport, we\'ll communicate any adjustments upfront before proceeding.'
    }
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };



  return (
    <div className="faq-page">
      <HeroBanner 
        title="Frequently Asked Questions" 
        breadcrumbs={['FAQ']}
      />
      
      <section className="ftco-section faq-section">
        <div className="container">
          <div className="row justify-content-center mb-5">
            <div className="col-md-8 heading-section text-center ftco-animate">
              <span className="subheading">Questions?</span>
              <h2 className="mb-4">Common Questions About Vehicle Shipping</h2>
              <p>Find answers to frequently asked questions about our auto transport services. Can't find what you're looking for? <a href="/contact">Contact our team</a> for more information.</p>
            </div>
          </div>

          <div className="row justify-content-center">
            <div className="col-md-10">
              <div className="faq-accordion">
                {faqs.map((faq, index) => (
                  <div key={index} className="faq-item ftco-animate">
                    <div 
                      className={`faq-question ${activeIndex === index ? 'active' : ''}`}
                      onClick={() => toggleAccordion(index)}
                    >
                      <h4>{faq.question}</h4>
                      <span className="faq-icon">
                        <i className="oi oi-plus"></i>
                      </span>
                    </div>
                    <div className={`faq-answer ${activeIndex === index ? 'show' : ''}`}>
                      <p>{faq.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="row justify-content-center mt-5">
            <div className="col-md-8 text-center">
              <div className="faq-contact-box">
                <h3>Still Have Questions?</h3>
                <p>Our dedicated support team is ready to help you with any inquiries.</p>
                <div className="contact-details mt-4">
                  <p><strong>Phone:</strong> +1 (888) 512-NEST</p>
                  <p><strong>Email:</strong> info@nestautotransport.com</p>
                  <p><strong>Hours:</strong> Available 24/7</p>
                </div>
                <Link to="/contact#contact-form" className="btn faq-btn mt-4">Contact Us</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
